import Text from 'components/Text/Text';
import React, { useContext, useEffect } from 'react';
import { IEditDishContext, EditDishContext } from '../../../../editDishState/editDishContext';
import { getFileIdForSubmit, preventDefaultForEnterKey } from 'utils/helpers';
import { Form } from 'antd';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Button from 'components/Button/Button';
import { CustomerSelect } from './components/CustomerSelect';
import { NameDishInput } from './components/NameDishInput';
import { PosNameInput } from './components/PosNameInput';
import { ArticleNumberInput } from './components/ArticleNumberInput';
import { DescriptionField } from './components/DescriptionField';
import { ExternalIdField } from './components/ExternalIdField';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import { HasSchedulePriorityCheckBox } from './components/HasSchedulePriorityCheckBox';
import { ActivationScheduleInput } from './components/ActivationScheduleInput';
import { DeactivationScheduleInput } from './components/DeactivationScheduleInput';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import { RestaurantsMultipleSelect } from './components/RestaurantsMultipleSelect';
import { SuggestedDishesField } from './components/SuggestedDishesField';
import { AdditionalDishesField } from './components/AdditionalDishesField';
import { ModificationDishesField } from './components/ModificationDishesField';
import { BtnBlock } from './components/BtnBlock';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './InputFields.module.scss';

export const InputFields: React.FC = () => {
	const {
		changeLoaderStatus,
		changeTypeOfModalWindow,
		setDataForSubmit,
		changeNameOfDish,
		changeDescription,
		setFetchedCustomers,
		setIDCustomer,
		changeExternalId,
		generateUniqueExternalId,
		setDefaultCustomerLanguage,
		setTranslationTab,
		changeNameTranslation,
		changeDescriptionTranslation,
		toggleHasSchedulePriority,
		changeActivationSchedule,
		changeDeactivationSchedule,
		setIDsRestaurant,
		setFetchedRestaurants,
		setFetchedCategories,
		setSuggestedDishCategory,
		setFetchedSuggestedDishSubCategories,
		setSuggestedDishSubCategory,
		setFetchedSuggestedDishes,
		setSuggestedDishes,
		removeSuggestedDish,
		setAdditionalDishCategory,
		setFetchedAdditionalDishSubCategories,
		setAdditionalDishSubCategory,
		setFetchedAdditionalDishes,
		setAdditionalDishes,
		removeAdditionalDish,
		setModificationDishCategory,
		setFetchedModificationDishSubCategories,
		setModificationDishSubCategory,
		setFetchedModificationDishes,
		setModificationDishes,
		removeModificationDish,
		dishID,
		fileId,
		filePath,
		nameOfDish,
		description,
		initialIDSelectedCustomer,
		IDSelectedCustomer,
		posName,
		externalId,
		articleNumber,
		selectedTranslationTab,
		translations,
		defaultCustomerLanguage,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		initialIDsSelectedRestaurant,
		IDsSelectedRestaurant,
		selectedSuggestedDishCategory,
		selectedSuggestedDishSubCategory,
		fetchedSuggestedDishes,
		selectedSuggestedDishes,
		selectedAdditionalDishCategory,
		selectedAdditionalDishSubCategory,
		fetchedAdditionalDishes,
		selectedAdditionalDishes,
		selectedModificationDishCategory,
		selectedModificationDishSubCategory,
		fetchedModificationDishes,
		selectedModificationDishes,
	} = useContext<IEditDishContext>(EditDishContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			nameOfDish,
			IDSelectedCustomer,
			posName,
			externalId,
			articleNumber,
			hasSchedulePriority,
			activationSchedule,
			deactivationSchedule,
			IDsSelectedRestaurant,
			selectedSuggestedDishCategory,
			selectedSuggestedDishSubCategory,
			selectedSuggestedDishes,
			selectedAdditionalDishCategory,
			selectedAdditionalDishSubCategory,
			selectedAdditionalDishes,
			selectedModificationDishCategory,
			selectedModificationDishSubCategory,
			selectedModificationDishes,
		});
	}, [
		nameOfDish,
		IDSelectedCustomer,
		posName,
		externalId,
		articleNumber,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		IDsSelectedRestaurant,
		selectedSuggestedDishCategory,
		selectedSuggestedDishSubCategory,
		selectedSuggestedDishes,
		selectedAdditionalDishCategory,
		selectedAdditionalDishSubCategory,
		selectedAdditionalDishes,
		selectedModificationDishCategory,
		selectedModificationDishSubCategory,
		selectedModificationDishes,
	]);

	const submit = async () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		setDataForSubmit({
			id: dishID,
			fileId: getFileIdForSubmit({ fileId, filePath }),
			name: nameOfDish,
			description: description || null,
			articleNumber: articleNumber || null,
			externalId: externalId || null,
			translations,
			overrideScheduleAvailability: hasSchedulePriority,
			activationCron: activationSchedule || null,
			deactivationCron: deactivationSchedule || null,
			restaurantIds: IDsSelectedRestaurant,
			suggestedDishIds: selectedSuggestedDishes,
			additionalDishIds: selectedAdditionalDishes,
			dishModificationIds: selectedModificationDishes,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<div className={style.infoWrapper}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#circleInfoTransparent`} />
						</svg>
						<Text
							translationNameSpace="form"
							translationKey="warningBeforeDishChange"
							theme="secondary"
							size="s"
							display="inline"
						/>
					</div>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<NameDishInput changeNameOfDish={changeNameOfDish} />
						<PosNameInput />
					</div>
					<div className={style.row}>
						<CustomerSelect
							setIDCustomer={setIDCustomer}
							setFetchedCustomers={setFetchedCustomers}
							initialIDSelectedCustomer={initialIDSelectedCustomer}
						/>
						<ArticleNumberInput />
					</div>
					<FullInfoField
						inputWrapperClassName={style.descriptionWrapper}
						label="descriptionLabel"
						useInForm={true}
						childrenContent={
							<DescriptionField changeDescription={changeDescription} description={description} />
						}
					/>
					<div className={style.row}>
						<ExternalIdField changeExternalId={changeExternalId} />
						<Button onClick={generateUniqueExternalId} theme="primary">
							<Text
								translationNameSpace="form"
								translationKey="generateUniqueExternalId"
								asFragment={true}
							/>
						</Button>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="translationsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<MenuTranslationForm
						className={style.translationForm}
						setDefaultCustomerLanguage={setDefaultCustomerLanguage}
						setTranslationTab={setTranslationTab}
						changeNameTranslation={changeNameTranslation}
						changeDescriptionTranslation={changeDescriptionTranslation}
						isShowLanguageName={false}
						IDSelectedCustomer={IDSelectedCustomer}
						translations={translations}
						selectedTranslationTab={selectedTranslationTab}
						nameLabel="dishNameLabel"
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<HasSchedulePriorityCheckBox
						toggleHasSchedulePriority={toggleHasSchedulePriority}
						hasSchedulePriority={hasSchedulePriority}
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantListHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<RestaurantsMultipleSelect
						setIDsRestaurant={setIDsRestaurant}
						setFetchedRestaurants={setFetchedRestaurants}
						changeLoaderStatus={changeLoaderStatus}
						IDSelectedCustomer={IDSelectedCustomer}
						initialIDsSelectedRestaurant={initialIDsSelectedRestaurant}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="suggestedDishesHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<SuggestedDishesField
						setFetchedCategories={setFetchedCategories}
						setFetchedSuggestedDishSubCategories={setFetchedSuggestedDishSubCategories}
						setFetchedSuggestedDishes={setFetchedSuggestedDishes}
						setSuggestedDishCategory={setSuggestedDishCategory}
						setSuggestedDishSubCategory={setSuggestedDishSubCategory}
						setSuggestedDishes={setSuggestedDishes}
						removeSuggestedDish={removeSuggestedDish}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
						selectedSuggestedDishCategory={selectedSuggestedDishCategory}
						selectedSuggestedDishSubCategory={selectedSuggestedDishSubCategory}
						selectedSuggestedDishes={selectedSuggestedDishes}
						fetchedSuggestedDishes={fetchedSuggestedDishes}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="additionalIngredientsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<AdditionalDishesField
						setFetchedAdditionalDishSubCategories={setFetchedAdditionalDishSubCategories}
						setFetchedAdditionalDishes={setFetchedAdditionalDishes}
						setAdditionalDishCategory={setAdditionalDishCategory}
						setAdditionalDishSubCategory={setAdditionalDishSubCategory}
						setAdditionalDishes={setAdditionalDishes}
						removeAdditionalDish={removeAdditionalDish}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
						selectedAdditionalDishCategory={selectedAdditionalDishCategory}
						selectedAdditionalDishSubCategory={selectedAdditionalDishSubCategory}
						selectedAdditionalDishes={selectedAdditionalDishes}
						fetchedAdditionalDishes={fetchedAdditionalDishes}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="dishModificationsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<ModificationDishesField
						setFetchedModificationDishSubCategories={setFetchedModificationDishSubCategories}
						setFetchedModificationDishes={setFetchedModificationDishes}
						setModificationDishCategory={setModificationDishCategory}
						setModificationDishSubCategory={setModificationDishSubCategory}
						setModificationDishes={setModificationDishes}
						removeModificationDish={removeModificationDish}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
						selectedModificationDishCategory={selectedModificationDishCategory}
						selectedModificationDishSubCategory={selectedModificationDishSubCategory}
						selectedModificationDishes={selectedModificationDishes}
						fetchedModificationDishes={fetchedModificationDishes}
					/>
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};
